import { useCallback } from 'react';
import { useCurrentUser, useCurrentUserId } from '@/entities/user';
import { useProjectCapabilities } from '@/entities/user';
import { FileComment, type MentionOption } from '@/entities/project-comment';
import { DeleteConfirmationModal } from '@/shared/ui';
import { AnnotatedCommentInput } from '../AnnotatedCommentInput';
import { parseAnnotation } from '@/features/comment-on-file';
import type { DraftShape } from '../../model/comments.store';
import {
  useAnnotationEdit,
  useAnnotationSolo,
  useAnnotationTool,
  useFlashCommentId,
  useHasComposingDrafts,
  useIsCommentSelected,
  useSelectionActions,
  useShowAnnotation,
} from '../../model/comments.store';
import type { Annotation } from '../../model/annotation';
import { formatRelativeTime } from '../../lib/formatRelativeTime';
import { commentCardClassName } from '../commentCardClassName';
import { CommentRepliesSkeleton } from './CommentRepliesSkeleton';
import { useThreadReplies } from './useThreadReplies';
import { useThreadActions } from './useThreadActions';
import type {
  AnnotatedCommentMediaKind,
  CommentData,
  CommentThreadActions,
} from './CommentThread.types';
import { useTranslation } from 'react-i18next';

const EMPTY_DOC: Record<string, unknown> = {
  type: 'doc',
  content: [{ type: 'paragraph' }],
};

interface Props {
  comment: CommentData;
  assetKind: AnnotatedCommentMediaKind;
  depth?: number;
  actions: CommentThreadActions;
  /** `@` dropdown options for the reply/edit composers (project members). */
  mentionOptions?: MentionOption[];
  /** True when this is the first reply in its thread — drops the top padding. */
  isFirstReply?: boolean;
}

/**
 * The annotation/media-player aware comment thread (asset path). Calls the
 * annotation + media-player hooks and therefore MUST render inside a
 * <MediaPlayerProvider>. Create/update/delete/resolve are injected via
 * `actions` so the same UI serves the file target (and any future
 * annotated target) without hard-wiring the file hooks.
 *
 * The target-agnostic replies + action logic is shared with the plain thread
 * via `useThreadReplies`/`useThreadActions`; this component layers the
 * annotation/media-specific hooks on top.
 */
export function AnnotatedCommentThread({
  comment,
  assetKind,
  depth = 0,
  actions,
  mentionOptions,
  isFirstReply = false,
}: Props) {
  const { t } = useTranslation('projects');
  const { t: tCommon } = useTranslation('common');

  const { beginEdit, endEdit } = useAnnotationEdit();
  const showAnnotation = useShowAnnotation();
  const { clearSolo } = useAnnotationSolo();
  const { selectComment } = useSelectionActions();
  const { selectTool } = useAnnotationTool();
  const hasComposingDrafts = useHasComposingDrafts();
  const flashCommentId = useFlashCommentId();
  const currentUserId = useCurrentUserId();
  const currentUser = useCurrentUser();
  const projects = useProjectCapabilities();

  const authorName = comment.author.name;
  const avatarUrl = comment.author.avatarUrl;

  const isRoot = depth === 0;
  const canComment = projects.canComment;
  const isAuthor = !!currentUserId && currentUserId === comment.createdBy;
  const canModifyOwn = isAuthor && canComment;
  const parsed = parseAnnotation(comment.annotation);
  // Text and field annotations are brief-only (a highlight anchor and a whole-section
  // comment, respectively) and never reach this file-comment thread, so the overlay only
  // considers shape-bearing kinds.
  const existingAnnotation =
    parsed && parsed.kind !== 'text' && parsed.kind !== 'field' ? parsed : null;
  // A comment with no annotation, or an image/pdf annotation that has no
  // shapes, has nothing to render on the overlay. Video annotations remain
  // routable even without shapes because they carry a timestamp.
  const routableAnnotation =
    existingAnnotation &&
    (existingAnnotation.kind === 'video' || existingAnnotation.shapes.length > 0)
      ? existingAnnotation
      : null;
  const isCommentSelected = useIsCommentSelected(comment.id);
  const isFlashing = isRoot && flashCommentId === comment.id;
  const isSelected = isRoot && isCommentSelected;

  const annotationCount = isRoot ? (existingAnnotation?.shapes.length ?? 0) : 0;
  const videoTimestampSeconds =
    isRoot && assetKind === 'video' && existingAnnotation?.kind === 'video'
      ? existingAnnotation.timestamp
      : undefined;

  const { replies, isLoadingReplies } = useThreadReplies(comment, isRoot, isSelected);
  const {
    handleConfirmDelete,
    handleReplySave,
    handleEditSave,
    handleCopyLink,
    handleResolve,
    showDeleteDialog,
    setShowDeleteDialog,
    isDeleting,
  } = useThreadActions(comment, actions);

  const handleEditStart = useCallback(() => {
    const prime = annotationToDraftPrime(existingAnnotation);
    beginEdit(comment.id, prime);
  }, [beginEdit, comment.id, existingAnnotation]);

  const handleEditEnd = useCallback(() => {
    endEdit();
  }, [endEdit]);

  const handleSelect = useCallback(() => {
    if (!isRoot || isSelected) return;
    selectComment(comment.id);
    selectTool(null);
    if (routableAnnotation && !hasComposingDrafts) {
      showAnnotation(comment.id, routableAnnotation, comment.versionId ?? '');
    } else {
      clearSolo();
    }
  }, [
    isRoot,
    isSelected,
    selectComment,
    selectTool,
    routableAnnotation,
    hasComposingDrafts,
    showAnnotation,
    clearSolo,
    comment.id,
    comment.versionId,
  ]);

  const isResolved = comment.status === 'resolved';

  const repliesSlot =
    isRoot && isSelected && (replies.length > 0 || isLoadingReplies) ? (
      <div className="flex flex-col gap-2">
        {replies.map((reply, index) => (
          <div key={reply.id} className="ml-7">
            <AnnotatedCommentThread
              comment={reply}
              assetKind={assetKind}
              depth={1}
              actions={actions}
              mentionOptions={mentionOptions}
              isFirstReply={index === 0}
            />
          </div>
        ))}
        {isLoadingReplies && <CommentRepliesSkeleton />}
      </div>
    ) : null;

  return (
    <div>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        data-comment-id={isRoot ? comment.id : undefined}
        onClick={isRoot ? handleSelect : undefined}
        className={commentCardClassName({ isRoot, isSelected, isFlashing, isFirstReply })}
      >
        <FileComment
          authorName={authorName}
          authorAvatarUrl={avatarUrl ?? undefined}
          currentUserName={currentUser?.name}
          timestamp={formatRelativeTime(comment.createdAt)}
          content={comment.content ?? EMPTY_DOC}
          attachments={comment.attachments}
          videoTimestampSeconds={videoTimestampSeconds}
          replyCount={comment.replyCount}
          selected={isSelected}
          annotationCount={annotationCount}
          onCopyLink={isRoot ? () => void handleCopyLink() : undefined}
          onDelete={canModifyOwn ? () => setShowDeleteDialog(true) : undefined}
          resolved={isRoot && isResolved}
          onResolve={isRoot ? handleResolve : undefined}
          resolveDisabled={!canComment}
          onEditStart={canModifyOwn ? handleEditStart : undefined}
          onEditEnd={canModifyOwn ? handleEditEnd : undefined}
          renderReplyInput={
            isRoot && canComment
              ? () => (
                  <AnnotatedCommentInput
                    assetKind={assetKind}
                    placeholder={t('commentOnFile.annotatedCommentThread.leaveYourReply')}
                    mentionOptions={mentionOptions}
                    showAnnotationToolbar={false}
                    supportsAnnotation={false}
                    clearSoloOnFocus={false}
                    onSave={handleReplySave}
                  />
                )
              : undefined
          }
          renderEditInput={
            canModifyOwn
              ? ({ initialContent, initialAttachments, onCancel, onSaved }) => (
                  <AnnotatedCommentInput
                    assetKind={assetKind}
                    placeholder={t('commentOnFile.annotatedCommentThread.editYourComment')}
                    submitLabel={t('commentOnFile.annotatedCommentThread.save')}
                    initialContent={initialContent}
                    initialAttachments={initialAttachments}
                    isEditing
                    showAnnotationToolbar={isRoot}
                    onCancel={onCancel}
                    onSave={async (content, annotation, attachmentAssetIds) => {
                      await handleEditSave(content, annotation, attachmentAssetIds);
                      onSaved();
                    }}
                  />
                )
              : undefined
          }
        >
          {repliesSlot}
        </FileComment>
      </div>

      <DeleteConfirmationModal
        open={showDeleteDialog}
        isDeleting={isDeleting}
        onCancel={() => {
          setShowDeleteDialog(false);
        }}
        onConfirmDelete={() => {
          void handleConfirmDelete();
        }}
        resourceType={tCommon('components.deleteConfirmationModal.comment')}
        warningMessage={t('commentOnFile.annotatedCommentThread.thisWillPermanentlyDeleteThe')}
      />
    </div>
  );
}

function annotationToDraftPrime(annotation: Annotation | null): {
  shapes: DraftShape[];
  timestamp?: number;
} {
  if (!annotation || annotation.kind === 'text' || annotation.kind === 'field') {
    return { shapes: [] };
  }
  if (annotation.kind === 'pdf') {
    return {
      shapes: annotation.shapes.map(({ shape, color, page }) => ({ shape, color, page })),
    };
  }
  const shapes: DraftShape[] = annotation.shapes.map(({ shape, color }) => ({ shape, color }));
  if (annotation.kind === 'video') return { shapes, timestamp: annotation.timestamp };
  return { shapes };
}
